
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUpdateContactToCustomer } from "./use-deal-status-updates";
import { useEffect } from "react";

export interface CreateDealData {
  title: string;
  contact_id?: string | null;
  organization_id?: string | null;
  value?: number | null;
  probability?: number | null;
  stage_id?: string | null;
  assigned_to_staff_id?: string | null;
  assigned_to_partner_id?: string | null;
  assigned_type?: 'staff' | 'partner' | null;
  created_by_staff_id?: string | null;
  created_by_partner_id?: string | null;
  deal_data?: any;
  total_cameras?: number | null;
  total_images?: number | null;
  total_revenue?: number | null;
  image_revenue?: number | null;
  description?: string | null;
  discount_amount?: number | null;
}

export const useDeals = (partnerId?: string) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['deals'],
    queryFn: async () => {
      console.log("🔍 [useDeals] Fetching deals with enhanced filtering...");
      
      const { data, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(
            id,
            first_name, 
            last_name, 
            email,
            user_id,
            user_status,
            intended_system_role,
            internal_owner_id,
            external_owner_id,
            organization_id,
            staff_owner:crm_staff!fk_profiles_internal_owner(
              id,
              first_name,
              last_name,
              email
            ),
            partner_owner:crm_partners!fk_profiles_external_owner(
              id,
              first_name,
              last_name,
              email
            )
          ),
          organization:organizations(name, company_id, has_msa, msa_reference),
          stage:crm_deal_stages(name, color),
          assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name, email),
          assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("❌ [useDeals] Error fetching deals:", error);
        throw error;
      }
      
      console.log("✅ [useDeals] Fetched deals successfully:", data);
      console.log(`📊 [useDeals] Total deals fetched: ${data?.length || 0}`);
      
      // Enhanced logging for deal status consistency
      const statusCounts = data?.reduce((acc, deal) => {
        acc[deal.status] = (acc[deal.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};
      
      console.log(`📊 [useDeals] Deal status distribution:`, statusCounts);
      
      // Log won deals with detailed info for debugging
      const wonDeals = data?.filter(deal => deal.status === 'won') || [];
      console.log(`🏆 [useDeals] Won deals found: ${wonDeals.length}`);
      wonDeals.forEach(deal => {
        console.log(`🏆 [useDeals] Won deal details:`, {
          id: deal.id,
          title: deal.title,
          status: deal.status,
          stage_id: deal.stage_id,
          probability: deal.probability,
          proposal_accepted_at: deal.proposal_accepted_at,
          isProperlyWon: deal.status === 'won' && deal.stage_id === null && deal.probability === 100
        });
      });

      // Log deals with inconsistent status/stage combinations
      const inconsistentDeals = data?.filter(deal => {
        if (deal.status === 'won' && (deal.stage_id !== null || deal.probability !== 100)) {
          return true;
        }
        if (deal.status === 'lost' && deal.stage_id !== null) {
          return true;
        }
        return false;
      }) || [];
      
      if (inconsistentDeals.length > 0) {
        console.warn(`⚠️ [useDeals] Found ${inconsistentDeals.length} deals with inconsistent status/stage:`, 
          inconsistentDeals.map(d => ({ id: d.id, title: d.title, status: d.status, stage_id: d.stage_id, probability: d.probability }))
        );
      }
      
      return data || [];
    },
    staleTime: 0, // Always fetch fresh data for contact status
    gcTime: 5 * 60 * 1000,
  });

  // Enhanced real-time subscription for deal updates with status focus
  useEffect(() => {
    console.log("🔄 [useDeals] Setting up enhanced real-time subscription for deal status updates");
    
    // Make channel name unique based on partnerId to avoid duplicate subscriptions
    const channelName = partnerId 
      ? `deals-status-updates-partner-${partnerId}-${Date.now()}` 
      : `deals-status-updates-enhanced-${Date.now()}`;
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deals'
        },
        (payload) => {
          console.log('🔄 [useDeals] Real-time update detected:', payload);
          const newDeal = payload.new as any;
          const oldDeal = payload.old as any;
          
          console.log('📊 [useDeals] Deal change details:', {
            event: payload.eventType,
            dealId: newDeal?.id || oldDeal?.id,
            newStatus: newDeal?.status,
            newStageId: newDeal?.stage_id,
            newProbability: newDeal?.probability,
            oldStatus: oldDeal?.status,
            oldStageId: oldDeal?.stage_id,
            oldProbability: oldDeal?.probability
          });
          
          // Special handling for deals marked as won - critical for proposal acceptance
          if (payload.eventType === 'UPDATE' && newDeal?.status === 'won' && oldDeal?.status !== 'won') {
            console.log('🏆 [useDeals] CRITICAL: Deal marked as WON via proposal acceptance');
            console.log('🎯 [useDeals] Verifying won deal consistency:', {
              status: newDeal.status,
              stage_id: newDeal.stage_id,
              probability: newDeal.probability,
              isProperlyWon: newDeal.status === 'won' && newDeal.stage_id === null && newDeal.probability === 100
            });
            
            // Immediate invalidation and refetch for won deals
            queryClient.invalidateQueries({ queryKey: ['deals'] });
            
            // Additional delayed refresh to ensure data consistency
            setTimeout(() => {
              console.log('🔄 [useDeals] Delayed refresh for won deal verification');
              queryClient.refetchQueries({ queryKey: ['deals'] });
            }, 1500);
          } else {
            // Standard refresh for other changes
            queryClient.invalidateQueries({ queryKey: ['deals'] });
          }
        }
      )
      // Also listen for profile updates to refresh contact status
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles'
        },
        (payload) => {
          console.log('👤 [useDeals] Profile real-time update detected:', payload);
          const newProfile = payload.new as any;
          const oldProfile = payload.old as any;
          
          // Check if user_status, user_id, or intended_system_role changed
          const statusChanged = newProfile?.user_status !== oldProfile?.user_status;
          const userIdChanged = newProfile?.user_id !== oldProfile?.user_id;
          const roleChanged = newProfile?.intended_system_role !== oldProfile?.intended_system_role;
          
          if (statusChanged || userIdChanged || roleChanged) {
            console.log('📊 [useDeals] Profile status/role change detected:', {
              profileId: newProfile?.id || oldProfile?.id,
              statusChanged,
              userIdChanged,
              roleChanged,
              newStatus: newProfile?.user_status,
              newUserId: newProfile?.user_id,
              newRole: newProfile?.intended_system_role
            });
            
            // Refresh deals to update contact badges
            queryClient.invalidateQueries({ queryKey: ['deals'] });
          }
        }
      )
      .subscribe();

    return () => {
      console.log("🛑 [useDeals] Cleaning up enhanced real-time subscription");
      // Properly unsubscribe before removing channel
      channel.unsubscribe().then(() => {
        supabase.removeChannel(channel);
      });
    };
  }, [queryClient, partnerId]);

  return query;
};

export const useDealDetails = (dealId?: string) => {
  return useQuery({
    queryKey: ['deal-details', dealId],
    queryFn: async () => {
      if (!dealId) return null;
      
      console.log("Fetching deal details for:", dealId);
      
      // First get the main deal data
      const { data: deal, error: dealError } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(
            id, 
            first_name, 
            last_name, 
            email,
            internal_owner_id,
            external_owner_id,
            staff_owner:crm_staff!fk_profiles_internal_owner(
              id,
              first_name,
              last_name,
              email
            ),
            partner_owner:crm_partners!fk_profiles_external_owner(
              id,
              first_name,
              last_name,
              email
            )
          ),
          organization:organizations(*),
          stage:crm_deal_stages(name, color),
          assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name, email),
          assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name, email)
        `)
        .eq('id', dealId)
        .single();

      if (dealError) {
        console.error("Error fetching deal details:", dealError);
        throw dealError;
      }

      // Get all deal stages for the dropdown
      const { data: dealStages, error: stagesError } = await supabase
        .from('crm_deal_stages')
        .select('*')
        .order('order_index', { ascending: true });

      if (stagesError) {
        console.error("Error fetching deal stages:", stagesError);
        throw stagesError;
      }

      // Get all contacts for the dropdown with owner information
      const { data: contactList, error: contactsError } = await supabase
        .from('profiles')
        .select(`
          *,
          staff_owner:crm_staff!fk_profiles_internal_owner(
            id,
            first_name,
            last_name,
            email
          ),
          partner_owner:crm_partners!fk_profiles_external_owner(
            id,
            first_name,
            last_name,
            email
          )
        `)
        .order('first_name', { ascending: true });

      if (contactsError) {
        console.error("Error fetching contacts:", contactsError);
        throw contactsError;
      }

      // Get all organizations for the dropdown
      const { data: organizationList, error: orgsError } = await supabase
        .from('organizations')
        .select('*')
        .order('name', { ascending: true });

      if (orgsError) {
        console.error("Error fetching organizations:", orgsError);
        throw orgsError;
      }
      
      const result = {
        ...deal,
        deal_stages: dealStages || [],
        contact_list: contactList || [],
        organization_list: organizationList || []
      };
      
      console.log("Fetched deal details with owner data:", result);
      return result;
    },
    enabled: !!dealId,
  });
};

// Helper function to get contact IDs owned by partner
const getPartnerContactIds = async (partnerId: string): Promise<string> => {
  const { data } = await supabase
    .from('profiles')
    .select('id')
    .eq('external_owner_id', partnerId);
  
  return (data || []).map(contact => contact.id).join(',') || 'none';
};

export const useArchivedDeals = () => {
  return useQuery({
    queryKey: ['archived-deals'],
    queryFn: async () => {
      console.log("Fetching archived deals (including deals with archived proposals)...");
      
      // First, get manually archived deals
      const { data: archivedDeals, error: archivedError } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles(
            id,
            first_name, 
            last_name, 
            email,
            internal_owner_id,
            external_owner_id
          ),
          organization:organizations(name, company_id),
          stage:crm_deal_stages(name, color)
        `)
        .eq('archived', true)
        .order('archived_at', { ascending: false });

      if (archivedError) {
        console.error("Error fetching manually archived deals:", archivedError);
        throw archivedError;
      }

      // Then, get deals with archived proposal notifications
      const { data: proposalArchivedDeals, error: proposalError } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles(
            id,
            first_name, 
            last_name, 
            email,
            internal_owner_id,
            external_owner_id
          ),
          organization:organizations(name, company_id),
          stage:crm_deal_stages(name, color),
          proposal_notifications!inner(status)
        `)
        .eq('proposal_notifications.status', 'archived')
        .eq('archived', false) // Only get deals that aren't manually archived to avoid duplicates
        .order('created_at', { ascending: false });

      if (proposalError) {
        console.error("Error fetching deals with archived proposals:", proposalError);
        throw proposalError;
      }

      // Combine both lists and remove duplicates
      const allArchivedDeals = [
        ...(archivedDeals || []),
        ...(proposalArchivedDeals || [])
      ];

      // Remove duplicates by deal ID
      const uniqueDeals = allArchivedDeals.filter((deal, index, self) => 
        index === self.findIndex(d => d.id === deal.id)
      );

      console.log("Fetched archived deals successfully:", {
        manuallyArchived: archivedDeals?.length || 0,
        proposalArchived: proposalArchivedDeals?.length || 0,
        total: uniqueDeals.length
      });
      
      return uniqueDeals;
    },
  });
};

export const useDealStages = () => {
  return useQuery({
    queryKey: ['deal-stages'],
    queryFn: async () => {
      console.log("Fetching deal stages...");
      const { data, error } = await supabase
        .from('crm_deal_stages')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) {
        console.error("Error fetching deal stages:", error);
        throw error;
      }
      
      console.log("Fetched deal stages:", data);
      return data;
    },
  });
};

export const useCreateDeal = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateDealData) => {
      console.log("🎯 [useCreateDeal] Starting deal creation process...");
      console.log("📝 [useCreateDeal] Input data:", JSON.stringify(data, null, 2));
      
      // Get current user to set partner assignment if creating user is a partner
      console.log("👤 [useCreateDeal] Getting current user...");
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("❌ [useCreateDeal] Error getting user:", userError);
        throw new Error(`Authentication error: ${userError.message}`);
      }
      
      if (!user) {
        console.error("❌ [useCreateDeal] No authenticated user found");
        throw new Error("You must be logged in to create deals");
      }
      
      console.log("✅ [useCreateDeal] Current user:", user.id, user.email);

      // Partner status is determined by the partner record, not by role.
      // This lets organisation users also act as partners without needing
      // a second role row.
      console.log("🔍 [useCreateDeal] Checking partner record for user:", user.id);
      const { data: partner, error: partnerError } = await supabase
        .from('crm_partners')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (partnerError) {
        console.error("❌ [useCreateDeal] Error getting partner record:", partnerError);
      }

      let partnerData = partner || null;
      console.log("👤 [useCreateDeal] Partner record:", partnerData?.id || null);

      if (partnerData) {
        console.log("🔍 [useCreateDeal] Auto-assigning deal to partner:", partnerData.id);
        // Pre-assign the partner fields to ensure RLS policy validation
        data.created_by_partner_id = partnerData.id;
        data.assigned_to_partner_id = data.assigned_to_partner_id || partnerData.id;
        data.assigned_type = 'partner';
        console.log("✅ [useCreateDeal] Pre-assigned partner fields:", {
          created_by_partner_id: partnerData.id,
          assigned_to_partner_id: data.assigned_to_partner_id
        });
      }
      
      if (!data.assigned_to_partner_id && !data.assigned_to_staff_id && !partnerData) {
        console.log("🔍 [useCreateDeal] No assignment provided, checking user role...");
        
        // Check if user is staff
        console.log("🔍 [useCreateDeal] Checking if user is staff...");
        const { data: staffRecord, error: staffError } = await supabase
          .from('crm_staff')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (staffError) {
          console.error("❌ [useCreateDeal] Error checking staff:", staffError);
          throw new Error(`Staff lookup failed: ${staffError.message}`);
        }
          
        if (staffRecord) {
          data.assigned_to_staff_id = staffRecord.id;
          data.assigned_type = 'staff';
          data.created_by_staff_id = staffRecord.id;
          console.log("✅ [useCreateDeal] Auto-assigned deal to staff:", staffRecord.id);
        } else {
          console.warn("⚠️ [useCreateDeal] User is neither partner nor staff - proceeding with unassigned deal");
        }
      }
      
      // Validate foreign key references before inserting
      console.log("🔍 [useCreateDeal] Validating foreign key references...");
      
      // Validate contact ownership for partners before proceeding
      if (data.contact_id) {
        console.log("🔍 [useCreateDeal] Validating contact_id:", data.contact_id);
        const { data: contact, error: contactError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, external_owner_id')
          .eq('id', data.contact_id)
          .maybeSingle();
          
        if (contactError) {
          console.error("❌ [useCreateDeal] Error validating contact:", contactError);
          throw new Error(`Contact validation failed: ${contactError.message}`);
        }
        
        if (!contact) {
          console.error("❌ [useCreateDeal] Contact not found:", data.contact_id);
          throw new Error("Selected contact does not exist");
        }
        
        // For partners, verify they own the contact
        if (partnerData) {
          if (contact.external_owner_id !== partnerData.id) {
            const contactName = `${contact.first_name} ${contact.last_name}`.trim();
            console.error("❌ [useCreateDeal] Partner does not own contact:", contact.external_owner_id, "vs", partnerData.id);
            throw new Error(`You can only create deals for contacts you own. ${contactName} is not assigned to you.`);
          }
          console.log("✅ [useCreateDeal] Contact ownership validated for partner");
        }
        
        console.log("✅ [useCreateDeal] Contact validated");
      }
      
      // Validate organization_id if provided
      if (data.organization_id) {
        console.log("🔍 [useCreateDeal] Validating organization_id:", data.organization_id);
        const { data: org, error: orgError } = await supabase
          .from('organizations')
          .select('id')
          .eq('id', data.organization_id)
          .maybeSingle();
          
        if (orgError) {
          console.error("❌ [useCreateDeal] Error validating organization:", orgError);
          throw new Error(`Organization validation failed: ${orgError.message}`);
        }
        
        if (!org) {
          console.error("❌ [useCreateDeal] Organization not found:", data.organization_id);
          throw new Error("Selected organization does not exist");
        }
        console.log("✅ [useCreateDeal] Organization validated");
      }
      
      // Validate assigned_to_partner_id if provided
      if (data.assigned_to_partner_id) {
        console.log("🔍 [useCreateDeal] Validating assigned_to_partner_id:", data.assigned_to_partner_id);
        const { data: partner, error: partnerError } = await supabase
          .from('crm_partners')
          .select('id')
          .eq('id', data.assigned_to_partner_id)
          .maybeSingle();
          
        if (partnerError) {
          console.error("❌ [useCreateDeal] Error validating partner:", partnerError);
          throw new Error(`Partner validation failed: ${partnerError.message}`);
        }
        
        if (!partner) {
          console.error("❌ [useCreateDeal] Partner not found:", data.assigned_to_partner_id);
          data.assigned_to_partner_id = null; // Clear invalid reference
          console.log("⚠️ [useCreateDeal] Cleared invalid partner assignment");
        } else {
          console.log("✅ [useCreateDeal] Partner validated");
        }
      }
      
      // Validate assigned_to_staff_id if provided
      if (data.assigned_to_staff_id) {
        console.log("🔍 [useCreateDeal] Validating assigned_to_staff_id:", data.assigned_to_staff_id);
        const { data: staff, error: staffError } = await supabase
          .from('crm_staff')
          .select('id')
          .eq('id', data.assigned_to_staff_id)
          .maybeSingle();
          
        if (staffError) {
          console.error("❌ [useCreateDeal] Error validating staff:", staffError);
          throw new Error(`Staff validation failed: ${staffError.message}`);
        }
        
        if (!staff) {
          console.error("❌ [useCreateDeal] Staff not found:", data.assigned_to_staff_id);
          data.assigned_to_staff_id = null; // Clear invalid reference
          console.log("⚠️ [useCreateDeal] Cleared invalid staff assignment");
        } else {
          console.log("✅ [useCreateDeal] Staff validated");
        }
      }

      // If no stage_id is provided, default to the first stage (Opportunity)
      if (!data.stage_id) {
        console.log("🎪 [useCreateDeal] No stage_id provided, fetching default stage...");
        const { data: stages, error: stageError } = await supabase
          .from('crm_deal_stages')
          .select('id, name')
          .order('order_index', { ascending: true })
          .limit(1);
        
        if (stageError) {
          console.error("❌ [useCreateDeal] Error fetching default stage:", stageError);
          throw new Error(`Failed to fetch default stage: ${stageError.message}`);
        }
        
        if (stages && stages.length > 0) {
          data.stage_id = stages[0].id;
          console.log("✅ [useCreateDeal] Set default stage_id to:", data.stage_id, "(" + stages[0].name + ")");
        } else {
          console.error("❌ [useCreateDeal] No deal stages found in database");
          throw new Error("No deal stages available - please contact administrator to set up deal stages");
        }
      }

      console.log("📤 [useCreateDeal] Final data before insert:", JSON.stringify(data, null, 2));
      
      // Retry logic for deal creation with improved error handling
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries) {
        try {
          console.log(`🔄 [useCreateDeal] Attempt ${retryCount + 1}/${maxRetries + 1} to create deal`);
          
          const { data: deal, error } = await supabase
            .from('crm_deals')
            .insert(data)
            .select()
            .single();

          if (error) {
            console.error(`❌ [useCreateDeal] Database error on attempt ${retryCount + 1}:`);
            console.error("❌ [useCreateDeal] Error code:", error.code);
            console.error("❌ [useCreateDeal] Error message:", error.message);
            console.error("❌ [useCreateDeal] Error details:", error.details);
            console.error("❌ [useCreateDeal] Error hint:", error.hint);
            
            // Check if this is an RLS error that might succeed on retry
            const isRlsError = error.message?.includes("row-level security") || 
                              error.message?.includes("insufficient_privilege") ||
                              error.code === 'PGRST301';
            
            if (isRlsError && retryCount < maxRetries) {
              console.log(`🔄 [useCreateDeal] RLS error detected, retrying in 500ms... (${retryCount + 1}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, 500));
              retryCount++;
              continue;
            }
            
            console.error("❌ [useCreateDeal] Full error object:", JSON.stringify(error, null, 2));
            console.error("❌ [useCreateDeal] Data that failed to insert:", JSON.stringify(data, null, 2));
            throw new Error(`Database error: ${error.message} (Code: ${error.code})`);
          }
          
          console.log("✅ [useCreateDeal] Deal created successfully:", deal);
          return deal;
          
        } catch (error) {
          if (retryCount === maxRetries) {
            console.error(`❌ [useCreateDeal] Final attempt failed after ${maxRetries + 1} tries`);
            throw error;
          }
          retryCount++;
        }
      }
    },
    onSuccess: (data) => {
      console.log("✅ [createDeal] Deal creation success with quote number:", data.quote_number, "Deal ID:", data.id);
      
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['partner-deals'] });
      queryClient.invalidateQueries({ queryKey: ['archived-deals'] });
      toast({
        title: "Success",
        description: `Deal "${data.title}" created successfully`,
      });
    },
    onError: (error: any) => {
      console.error("❌ [createDeal] Error creating deal:", error);
      
      // Provide more specific error messages based on the error type
      let errorMessage = "Failed to create deal";
      
      if (error?.message?.includes("insufficient_privilege") || error?.message?.includes("row-level security")) {
        errorMessage = "You don't have permission to create deals. Please contact your administrator.";
      } else if (error?.message?.includes("foreign key")) {
        errorMessage = "The selected contact or organization is invalid. Please refresh and try again.";
      } else if (error?.message?.includes("null value")) {
        errorMessage = "Please fill in all required fields.";
      } else if (error?.message) {
        errorMessage = `Creation failed: ${error.message}`;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateDeal = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const updateContactToCustomer = useUpdateContactToCustomer();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateDealData> }) => {
      const { data: deal, error } = await supabase
        .from('crm_deals')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return deal;
    },
    onSuccess: (updatedDeal, { data }) => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      
      // If deal is being marked as won/closed and has a contact, update contact to customer
      if (updatedDeal.status === 'won' && updatedDeal.contact_id) {
        updateContactToCustomer.mutate(updatedDeal.contact_id);
      }
      
      toast({
        title: "Success",
        description: "Deal updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating deal:", error);
      toast({
        title: "Error",
        description: "Failed to update deal",
        variant: "destructive",
      });
    },
  });
};

export const useMarkDealAsLost = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ dealId, reason }: { dealId: string; reason?: string }) => {
      console.log("Marking deal as lost:", dealId, "Reason:", reason);
      
      const updateData: any = { 
        status: 'lost',
        updated_at: new Date().toISOString()
      };

      // If reason is provided, store it in deal_data
      if (reason) {
        updateData.deal_data = { lost_reason: reason };
      }

      const { data: updatedDeal, error } = await supabase
        .from('crm_deals')
        .update(updateData)
        .eq('id', dealId)
        .select()
        .single();

      if (error) throw error;
      return updatedDeal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      toast({
        title: "Deal Lost",
        description: "Deal marked as lost",
      });
    },
    onError: (error) => {
      console.error("Error marking deal as lost:", error);
      toast({
        title: "Error",
        description: "Failed to mark deal as lost",
        variant: "destructive",
      });
    },
  });
};

export const useArchiveDeal = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ dealId, reason }: { dealId: string; reason?: string }) => {
      console.log("Archiving deal:", dealId, "Reason:", reason);
      
      const updateData: any = { 
        archived: true,
        archived_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // If reason is provided, store it in archived_reason
      if (reason) {
        updateData.archived_reason = reason;
      }

      const { data: updatedDeal, error } = await supabase
        .from('crm_deals')
        .update(updateData)
        .eq('id', dealId)
        .select()
        .single();

      if (error) {
        console.error("Database error:", error);
        throw new Error(`Failed to archive deal: ${error.message}`);
      }

      // Also update any associated proposal notifications to archived status
      console.log("Archiving associated proposal notifications for deal:", dealId);
      const { error: notificationError } = await supabase
        .from('proposal_notifications')
        .update({
          status: 'archived',
          updated_at: new Date().toISOString(),
          response_notes: reason ? `Deal archived: ${reason}` : 'Deal archived'
        })
        .eq('deal_id', dealId)
        .in('status', ['pending']); // Only archive pending notifications

      if (notificationError) {
        console.error("Error archiving proposal notifications:", notificationError);
        // Don't throw here - deal archiving should succeed even if notification update fails
      } else {
        console.log("Successfully archived proposal notifications for deal:", dealId);
      }
      
      return updatedDeal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['archived-deals'] });
      toast({
        title: "Deal Archived",
        description: "Deal has been successfully archived",
      });
    },
    onError: (error) => {
      console.error("Error archiving deal:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to archive deal",
        variant: "destructive",
      });
    },
  });
};

export const useRestoreDeal = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (dealId: string) => {
      console.log("Restoring deal:", dealId);
      
      const { data: updatedDeal, error } = await supabase
        .from('crm_deals')
        .update({ 
          archived: false,
          archived_at: null,
          archived_reason: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', dealId)
        .select()
        .single();

      if (error) {
        console.error("Database error:", error);
        throw new Error(`Failed to restore deal: ${error.message}`);
      }

      // Also restore any associated proposal notifications back to pending status
      console.log("Restoring proposal notifications for deal:", dealId);
      const { error: notificationError } = await supabase
        .from('proposal_notifications')
        .update({
          status: 'pending',
          updated_at: new Date().toISOString(),
          response_notes: 'Deal restored from archive'
        })
        .eq('deal_id', dealId)
        .eq('status', 'archived'); // Only restore archived notifications

      if (notificationError) {
        console.error("Error restoring proposal notifications:", notificationError);
        // Don't throw here - deal restoration should succeed even if notification update fails
      } else {
        console.log("Successfully restored proposal notifications for deal:", dealId);
      }
      
      return updatedDeal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['archived-deals'] });
      toast({
        title: "Deal Restored",
        description: "Deal has been successfully restored",
      });
    },
    onError: (error) => {
      console.error("Error restoring deal:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to restore deal",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteDeal = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_deals')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['archived-deals'] });
      toast({
        title: "Success",
        description: "Deal deleted successfully",
      });
    },
    onError: (error) => {
      console.error("Error deleting deal:", error);
      toast({
        title: "Error",
        description: "Failed to delete deal",
        variant: "destructive",
      });
    },
  });
};
